import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMemo } from 'react';

interface WorkbookWithTags {
  id: string;
  title: string;
  is_favorite: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
  tags: Array<{
    id: string;
    name: string;
    color: string;
  }>;
}

interface UnifiedCounts {
  all: number;
  archived: number;
  tagCounts: Record<string, number>;
  totalWorkbooks: number;
}

/**
 * Single comprehensive query to get all workbooks with their tags
 * This replaces multiple separate count queries for maximum performance
 * SCALABLE: Includes both owned and editor-accessible workbooks
 */
async function getAllWorkbooksWithTags(): Promise<WorkbookWithTags[]> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    throw new Error('User must be authenticated');
  }

  const userId = sessionData.session.user.id;

  // STEP 1: Get workbook IDs where user is an editor (first query)
  const { data: editorWorkbooks, error: editorWorkbookError } = await supabase
    .from('workbook_editors')
    .select('workbook_id')
    .eq('editor_id', userId);

  if (editorWorkbookError) {
    console.error('Failed to fetch editor workbooks for counts:', editorWorkbookError);
    // Continue with owned workbooks only as fallback
  }

  // STEP 2: Build array of accessible workbook IDs (owned + editor)
  const editorWorkbookIds = editorWorkbooks?.map(item => item.workbook_id) || [];
  console.log(`[getAllWorkbooksWithTags] Found ${editorWorkbookIds.length} editor-accessible workbooks for counting`);

  // STEP 3: Get all accessible workbooks with efficient filtering
  let workbooksQuery = supabase
    .from('workbook')
    .select(`
      id,
      title,
      is_favorite,
      is_archived,
      created_at,
      updated_at,
      user_id
    `)
    .order('updated_at', { ascending: false });

  // Core access filter: owned OR editor-accessible
  if (editorWorkbookIds.length > 0) {
    workbooksQuery = workbooksQuery.or(`user_id.eq.${userId},id.in.(${editorWorkbookIds.join(',')})`);
  } else {
    // Fallback: only owned workbooks if no editor access found
    workbooksQuery = workbooksQuery.eq('user_id', userId);
  }

  const { data: workbooksData, error: workbooksError } = await workbooksQuery;

  if (workbooksError) {
    console.error('Error fetching workbooks for counts:', workbooksError);
    throw workbooksError;
  }

  if (!workbooksData || workbooksData.length === 0) {
    return [];
  }

  console.log(`[getAllWorkbooksWithTags] Retrieved ${workbooksData.length} accessible workbooks for counting`);

  // Get tag assignments separately to avoid relationship ambiguity
  const workbookIds = workbooksData.map(w => w.id);
  const { data: tagAssignments, error: tagsError } = await supabase
    .from('workbook_tag_assignments')
    .select(`
      workbook_id,
      workbook_tags(
        id,
        name,
        color
      )
    `)
    .in('workbook_id', workbookIds);

  const error = tagsError;

  if (error) {
    console.error('Error fetching workbook tags:', error);
    throw error;
  }

  // Create a map of workbook ID to tags for efficient lookup
  const workbookTagsMap = new Map<string, Array<{id: string; name: string; color: string}>>();
  
  if (tagAssignments) {
    tagAssignments.forEach(assignment => {
      if (assignment.workbook_tags) {
        const existing = workbookTagsMap.get(assignment.workbook_id) || [];
        existing.push(assignment.workbook_tags);
        workbookTagsMap.set(assignment.workbook_id, existing);
      }
    });
  }

  // Normalize the data structure by combining workbooks with their tags
  const workbooks: WorkbookWithTags[] = workbooksData.map(workbook => ({
    ...workbook,
    tags: workbookTagsMap.get(workbook.id) || []
  }));

  return workbooks;
}

/**
 * Unified hook that provides all counts from a single dataset
 * Maximum performance: 1 database query instead of multiple separate count queries
 */
export function useWorkbookCountsUnified() {
  const { data: workbooks, isLoading, error } = useQuery({
    queryKey: ['workbooks-with-tags-unified'],
    queryFn: getAllWorkbooksWithTags,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Compute all counts from the single dataset
  const counts = useMemo((): UnifiedCounts => {
    if (!workbooks) {
      return {
        all: 0,
        archived: 0,
        tagCounts: {},
        totalWorkbooks: 0
      };
    }

    // Compute status-based counts
    const nonArchived = workbooks.filter(w => !w.is_archived);
    const archived = workbooks.filter(w => w.is_archived);

    // Compute tag counts (only from non-archived workbooks)
    const tagCounts: Record<string, number> = {};
    nonArchived.forEach(workbook => {
      workbook.tags.forEach(tag => {
        tagCounts[tag.id] = (tagCounts[tag.id] || 0) + 1;
      });
    });

    return {
      all: nonArchived.length,
      archived: archived.length,
      tagCounts,
      totalWorkbooks: workbooks.length
    };
  }, [workbooks]);

  return {
    data: counts,
    isLoading,
    error,
    workbooks // Expose raw data for other uses
  };
}

export default useWorkbookCountsUnified; 